import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import { apiMqtt } from '../../../services/api.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import {
    fetchEnvironmentControls,
    fetchEnvironmentDevices,
    addEnvironmentControl,
    editEnvironmentControl,
    deleteEnvironmentControls,
    createDefaultControls,
} from './api/environmentalSettingsApi.js';

import EnvironmentalTable from './components/EnvironmentalTable.jsx';
import EnvironmentalDrawer from './components/EnvironmentalDrawer.jsx';
import DeviceAssignDrawer from '../../../components/v2/DeviceAssignDrawer.jsx';

import './environmental-settings-page.css';

/* Environmental devices show (and search) their category first; see DeviceAssignDrawer. */
const ENV_DEVICE_FIELDS = ['device_category', 'descriptions', 'manufacture_uuid', 'entity_name'];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const TABS = [
    { key: 'temperature', label: 'Temperature' },
    { key: 'humidity', label: 'Humidity' },
    { key: 'battery', label: 'Battery' },
];

/** Device category filters per environmental type. */
const DEVICE_CATEGORIES = {
    temperature: '1,17',
    humidity: '17',
    battery: '1,2,3,6,7,8,9,10,11,12,15,16,17',
};

/** environmental_type_id per tab key. */
const ENV_TYPE_ID = { temperature: 1, humidity: 2, battery: 3 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const EnvironmentalSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    const session = useMemo(() => getSession(), []);

    const campusList = useMemo(() => {
        return getCampusList() || [];
    }, []);

    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // Permissions
    const editRights = useMemo(() => iAmHigherAuthority(20, session?.role_weight), [session]);
    const adminRights = useMemo(() => iAmHigherAuthority(99, session?.role_weight), [session]);

    // -- Tab state --
    const [activeTab, setActiveTab] = useState('temperature');

    // -- Data stores --
    const [tempData, setTempData] = useState([]);
    const [humidityData, setHumidityData] = useState([]);
    const [batteryData, setBatteryData] = useState([]);
    const [tempDevices, setTempDevices] = useState([]);
    const [humidityDevices, setHumidityDevices] = useState([]);
    const [batteryDevices, setBatteryDevices] = useState([]);

    // -- Loading / Saving --
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [restoring, setRestoring] = useState(false);

    // -- Environmental Drawer (Add/Edit) --
    const [envDrawerOpen, setEnvDrawerOpen] = useState(false);
    const [envEditTarget, setEnvEditTarget] = useState(null);
    const [envDrawerType, setEnvDrawerType] = useState('temperature');

    // -- Device Assign Drawer --
    const [deviceDrawerOpen, setDeviceDrawerOpen] = useState(false);
    const [deviceDrawerTarget, setDeviceDrawerTarget] = useState(null);
    const [deviceDrawerType, setDeviceDrawerType] = useState('temperature');

    // -- Delete Confirm Dialog --
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteIds, setDeleteIds] = useState([]);

    // -- Toast --
    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        try {
            const [
                tempRes, humRes, batRes,
                tempDevRes, humDevRes, batDevRes,
            ] = await Promise.all([
                fetchEnvironmentControls(cId, 1),
                fetchEnvironmentControls(cId, 2),
                fetchEnvironmentControls(cId, 3),
                fetchEnvironmentDevices(cId, DEVICE_CATEGORIES.temperature),
                fetchEnvironmentDevices(cId, DEVICE_CATEGORIES.humidity),
                fetchEnvironmentDevices(cId, DEVICE_CATEGORIES.battery),
            ]);

            setTempData(tempRes || []);
            setHumidityData(humRes || []);
            setBatteryData(batRes || []);
            setTempDevices(tempDevRes || []);
            setHumidityDevices(humDevRes || []);
            setBatteryDevices(batDevRes || []);
        } catch (err) {
            console.error('[EnvironmentalSettingsPage] Load error:', err);
            showToast('Failed to load environmental settings data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // Initial load + reload on campus switch
    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch
    // ======================================================================
    const handleCampusChange = (newId) => {
        setCampusId(newId);
        updateSessionCampus(newId);

        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;
        if (oldIsCloud !== newIsCloud) {
            window.location.reload();
        }
    };

    // ======================================================================
    // MQTT Operations — Sync Local & Restore Data
    // ======================================================================
    const handleSyncLocal = async () => {
        setSyncing(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'reboot-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Sync triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error syncing. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error syncing. Contact support if the error persists.', 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleRestoreData = async () => {
        setRestoring(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'restore-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Restore triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error restoring. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error restoring. Contact support if the error persists.', 'error');
        } finally {
            setRestoring(false);
        }
    };

    // ======================================================================
    // CRUD Handlers
    // ======================================================================
    const meta = useMemo(() => ({
        campusId,
        userId: getUserId(),
        clientId: getSessionId(),
    }), [campusId]);

    // --- Create Defaults ---
    const handleCreateDefaults = async (envType) => {
        setSaving(true);
        const envTypeId = ENV_TYPE_ID[envType] || 1;
        try {
            const [ok] = await createDefaultControls(envTypeId, meta);
            if (ok) {
                showToast('Default settings created.');
                await loadData(campusId);
            } else {
                showToast('Failed to create default settings.', 'error');
            }
        } catch {
            showToast('Failed to create default settings.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Add Control ---
    const handleAddControl = async (formData) => {
        setSaving(true);
        try {
            const [ok] = await addEnvironmentControl([formData], meta);
            if (ok) {
                setEnvDrawerOpen(false);
                showToast('Setting added.');
                await loadData(campusId);
            } else {
                showToast('Failed to add setting.', 'error');
            }
        } catch {
            showToast('Failed to add setting.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Edit Control ---
    const handleEditControl = async (formData) => {
        setSaving(true);
        const old = envEditTarget || {};
        const oldPayload = [{
            control_id: old.control_id,
            description: old.description,
            low_alarm: old.low_alarm,
            high_alarm: old.high_alarm,
            alarm_on: old.alarm_on,
            test_after: old.test_after,
            campus_id: campusId,
            environmental_type_id: old.environmental_type_id,
            environmental_alarm_type_id: old.environmental_alarm_type_id,
            device_ids: old.device_ids || '',
            modified_by: old.modified_by || '',
        }];

        try {
            const [ok] = await editEnvironmentControl([formData], oldPayload, meta);
            if (ok) {
                setEnvDrawerOpen(false);
                setEnvEditTarget(null);
                showToast('Setting updated.');
                await loadData(campusId);
            } else {
                showToast('Failed to update setting.', 'error');
            }
        } catch {
            showToast('Failed to update setting.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Delete Controls ---
    const handleRequestDelete = (controlIds) => {
        setDeleteIds(controlIds);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setDeleteDialogOpen(false);
        setSaving(true);
        try {
            const [ok] = await deleteEnvironmentControls(deleteIds, getUserId(), meta);
            if (ok) {
                showToast(`${deleteIds.length} setting${deleteIds.length !== 1 ? 's' : ''} deleted.`);
                await loadData(campusId);
            } else {
                showToast('Failed to delete setting(s).', 'error');
            }
        } catch {
            showToast('Failed to delete setting(s).', 'error');
        } finally {
            setSaving(false);
            setDeleteIds([]);
        }
    };

    // --- Save Devices ---
    const handleSaveDevices = async (controlId, selectedDeviceIds, oldDeviceIds) => {
        setSaving(true);
        const newDeviceStr = selectedDeviceIds.join(',');
        const oldDeviceStr = oldDeviceIds.join(',');

        const target = deviceDrawerTarget || {};
        const payload = [{
            control_id: controlId,
            device_ids: newDeviceStr,
            campus_id: campusId,
            modified_by: getUserId(),
        }];
        const oldPayload = [{
            control_id: controlId,
            device_ids: oldDeviceStr,
            campus_id: campusId,
            modified_by: target.modified_by || '',
        }];

        try {
            const [ok] = await editEnvironmentControl(payload, oldPayload, meta);
            if (ok) {
                setDeviceDrawerOpen(false);
                setDeviceDrawerTarget(null);
                showToast('Device assignments updated.');
                await loadData(campusId);
            } else {
                showToast('Failed to update device assignments.', 'error');
            }
        } catch {
            showToast('Failed to update device assignments.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ======================================================================
    // Data selectors per tab
    // ======================================================================
    const getDataForTab = (tabKey) => {
        switch (tabKey) {
            case 'temperature': return tempData;
            case 'humidity': return humidityData;
            case 'battery': return batteryData;
            default: return [];
        }
    };

    const getDevicesForTab = (tabKey) => {
        switch (tabKey) {
            case 'temperature': return tempDevices;
            case 'humidity': return humidityDevices;
            case 'battery': return batteryDevices;
            default: return [];
        }
    };

    // ======================================================================
    // Render helpers
    // ======================================================================
    const renderTabContent = () => {
        if (loading) {
            return (
                <div className="pp-section">
                    <div className="pp-section__body">
                        <div className="ss-skeleton" style={{ width: '60%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '80%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px' }} />
                    </div>
                </div>
            );
        }

        return (
            <EnvironmentalTable
                data={getDataForTab(activeTab)}
                envType={activeTab}
                devices={getDevicesForTab(activeTab)}
                onAdd={() => {
                    setEnvEditTarget(null);
                    setEnvDrawerType(activeTab);
                    setEnvDrawerOpen(true);
                }}
                onEdit={(row) => {
                    setEnvEditTarget(row);
                    setEnvDrawerType(activeTab);
                    setEnvDrawerOpen(true);
                }}
                onDelete={(ids) => handleRequestDelete(ids)}
                onAssignDevices={(row) => {
                    setDeviceDrawerTarget(row);
                    setDeviceDrawerType(activeTab);
                    setDeviceDrawerOpen(true);
                }}
                onCreateDefaults={() => handleCreateDefaults(activeTab)}
                saving={saving}
            />
        );
    };

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Page header */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <nav className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Settings</span>
                                    <span className="ps-breadcrumb__separator">/</span>
                                    <span className="ps-breadcrumb__current">Environmental Settings</span>
                                </nav>
                                <div className="ps-campus-row">
                                    {/* MQTT action buttons */}
                                    {editRights ? (
                                        <button
                                            className="ap-btn ap-btn--secondary"
                                            onClick={handleSyncLocal}
                                            disabled={syncing}
                                            title="Syncs local server with cloud data, will cause the server to go offline momentarily during the process"
                                        >
                                            {syncing ? 'Syncing...' : 'Sync Local'}
                                        </button>
                                    ) : null}
                                    {adminRights ? (
                                        <button
                                            className="ap-btn ap-btn--secondary"
                                            onClick={handleRestoreData}
                                            disabled={restoring}
                                            title="Restore data performs the sync local reboot, however deletes the database file triggering a re-create."
                                        >
                                            {restoring ? 'Restoring...' : 'Restore Data'}
                                        </button>
                                    ) : null}

                                    {/* Campus selector */}
                                    <label htmlFor="env-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="env-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>
                                                    {c.campus_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="pp-tab-bar">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Scrollable tab content */}
                    <div className="pp-tab-content">
                        {renderTabContent()}
                    </div>

                    {/* Environmental Add/Edit Drawer */}
                    <EnvironmentalDrawer
                        open={envDrawerOpen}
                        onClose={() => { setEnvDrawerOpen(false); setEnvEditTarget(null); }}
                        onSave={envEditTarget ? handleEditControl : handleAddControl}
                        saving={saving}
                        editTarget={envEditTarget}
                        envType={envDrawerType}
                        campusId={campusId}
                    />

                    {/* Device Assign Drawer */}
                    <DeviceAssignDrawer
                        open={deviceDrawerOpen}
                        onClose={() => { setDeviceDrawerOpen(false); setDeviceDrawerTarget(null); }}
                        onSave={(ids, oldIds) => handleSaveDevices(deviceDrawerTarget?.control_id, ids, oldIds)}
                        saving={saving}
                        row={deviceDrawerTarget}
                        idsKey="device_ids"
                        title="Assign Devices"
                        contextLabel="Setting"
                        contextName={deviceDrawerTarget?.description}
                        detailFields={ENV_DEVICE_FIELDS}
                        devices={getDevicesForTab(deviceDrawerType)}
                    />

                    {/* Delete Confirmation Dialog */}
                    {deleteDialogOpen ? (
                        <div className="pp-dialog-backdrop" onClick={() => setDeleteDialogOpen(false)}>
                            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="pp-dialog__header">
                                    <h3 className="pp-dialog__title">Confirm Delete</h3>
                                    <button className="dp-drawer__close" onClick={() => setDeleteDialogOpen(false)}>
                                        <i className="fe fe-x" />
                                    </button>
                                </div>
                                <div className="pp-dialog__body">
                                    <p className="pp-dialog__text">
                                        Are you sure you want to delete {deleteIds.length} setting{deleteIds.length !== 1 ? 's' : ''}? This action cannot be undone.
                                    </p>
                                </div>
                                <div className="pp-dialog__footer">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setDeleteDialogOpen(false)}>
                                        Cancel
                                    </button>
                                    <button className="dp-btn dp-btn--danger" disabled={saving} onClick={handleConfirmDelete}>
                                        {saving ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default EnvironmentalSettingsPage;
